import React, { useState, useRef } from 'react';
import { 
  Container, 
  TextField, 
  Button, 
  Typography, 
  CircularProgress, 
  InputAdornment, 
  IconButton, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogContentText, 
  DialogActions, 
  Box 
} from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import { useAuth } from '../../context/AuthContext';
import { uploadJournalEntry, uploadAudioForEntry, ACCEPT_FILE_TYPES_STRING, ACCEPT_AUDIO_TYPES_STRING } from '../../services/bennyJournalService';

const CreateJournalEntry = () => {
  const { currentUser } = useAuth();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [file, setFile] = useState(null);
  const [audioFile, setAudioFile] = useState(null);
  const [microsoftLink, setMicrosoftLink] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const fileInputRef = useRef(null);
  const audioInputRef = useRef(null);
  const [infoDialogOpen, setInfoDialogOpen] = useState(false);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      // Basic check for PDF extension
      if (!selectedFile.name.toLowerCase().endsWith('.pdf')) {
        setFile(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = null; // Reset file input visually
        }
        return;
      }
      // Valid file selected
      setFile(selectedFile);
      // REMOVED: setMicrosoftLink(''); 
    } else {
      // No file selected (e.g., user cleared the input or cancelled dialog)
      setFile(null);
      // The file input is visually cleared by the browser in this case.
    }
  };

  const handleAudioFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      // Basic check for audio extension
      const ext = selectedFile.name.toLowerCase();
      if (!ext.endsWith('.mp3') && !ext.endsWith('.m4a') && !ext.endsWith('.wav')) {
        setAudioFile(null);
        if (audioInputRef.current) {
            audioInputRef.current.value = null;
        }
        return;
      }
      setAudioFile(selectedFile);
    } else {
      setAudioFile(null);
    }
  };

  const handleMicrosoftLinkChange = (e) => {
    setMicrosoftLink(e.target.value);
  };

  const handleOpenInfoDialog = () => {
    setInfoDialogOpen(true);
  };

  const handleCloseInfoDialog = () => {
    setInfoDialogOpen(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!title || (!file && !microsoftLink)) { 
      return;
    }
    
    if (!currentUser || !currentUser.uid) {
      return;
    }
    // Updated check to use currentUser.displayName
    if (!currentUser.displayName) {
      return; 
    }

    setIsUploading(true);
    setUploadProgress(0);
    try {
      // Use currentUser.displayName instead of firestoreUser.displayName
      const entryId = await uploadJournalEntry(file, title, description, currentUser.uid, currentUser.displayName, microsoftLink);
      
      // If audio file was selected, upload it to the entry
      if (audioFile && entryId) {
        setUploadProgress(50); // Show progress
        await uploadAudioForEntry(entryId, audioFile);
      }
      
      setUploadProgress(100);
      setTitle('');
      setDescription('');
      setFile(null);
      setAudioFile(null);
      setMicrosoftLink(''); // Reset Microsoft link field
      if (fileInputRef.current) {
        fileInputRef.current.value = null;
      }
      if (audioInputRef.current) {
        audioInputRef.current.value = null;
      }
    } catch (err) {
      // Handle error (e.g., show a notification)
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  return (
    <Container maxWidth="sm" sx={{ mt: 4, backgroundColor: '#333', p: 3, borderRadius: 2, color: 'white' }}>
      <Typography variant="h4" component="h1" gutterBottom sx={{ color: '#d98d2a', fontFamily: '"Georgia", serif' }}>
        Create Benny's Journal Entry
      </Typography>
      <form onSubmit={handleSubmit}>
        <TextField
          label="Title"
          variant="outlined"
          fullWidth
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          margin="normal"
          InputLabelProps={{ style: { color: 'white' } }}
          inputProps={{ style: { color: 'white' } }}
          sx={{ '& .MuiOutlinedInput-root': { '& fieldset': { borderColor: 'white' }, '&:hover fieldset': { borderColor: '#d98d2a' }, '&.Mui-focused fieldset': { borderColor: '#d98d2a' } } }}
        />
        <TextField
          label="Description"
          variant="outlined"
          fullWidth
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          margin="normal"
          multiline
          rows={4}
          InputLabelProps={{ style: { color: 'white' } }}
          inputProps={{ style: { color: 'white' } }}
          sx={{ '& .MuiOutlinedInput-root': { '& fieldset': { borderColor: 'white' }, '&:hover fieldset': { borderColor: '#d98d2a' }, '&.Mui-focused fieldset': { borderColor: '#d98d2a' } } }}
        />
        <Box sx={{ mt: 2, mb: 1 }}>
          <Typography variant="body2" sx={{ color: '#d98d2a', mb: 1 }}>
            Upload File (PDF only):
          </Typography>
          <Button
            variant="outlined"
            component="label"
            sx={{
              color: '#d98d2a',
              borderColor: '#d98d2a',
              '&:hover': { 
                borderColor: '#c87d26', 
                backgroundColor: 'rgba(217, 141, 42, 0.1)' 
              },
              mr: file ? 1 : 0, // Add margin only if a file is selected
            }}
          >
            Choose File
            <input
              type="file"
              hidden
              accept={ACCEPT_FILE_TYPES_STRING}
              onChange={handleFileChange}
              ref={fileInputRef}
            />
          </Button>
          {file && (
            <Typography variant="body2" component="span" sx={{ color: '#F5F5F5', verticalAlign: 'middle' }}>
              {file.name}
            </Typography>
          )}
        </Box>

        <Box sx={{ mt: 2, mb: 1 }}>
          <Typography variant="body2" sx={{ color: '#d98d2a', mb: 1 }}>
            Upload Audio Recording (Optional - MP3, M4A, or WAV):
          </Typography>
          <Button
            variant="outlined"
            component="label"
            sx={{
              color: '#d98d2a',
              borderColor: '#d98d2a',
              '&:hover': { 
                borderColor: '#c87d26', 
                backgroundColor: 'rgba(217, 141, 42, 0.1)' 
              },
              mr: audioFile ? 1 : 0,
            }}
          >
            🎙️ Choose Audio
            <input
              type="file"
              hidden
              accept={ACCEPT_AUDIO_TYPES_STRING}
              onChange={handleAudioFileChange}
              ref={audioInputRef}
            />
          </Button>
          {audioFile && (
            <Typography variant="body2" component="span" sx={{ color: '#F5F5F5', verticalAlign: 'middle' }}>
              {audioFile.name}
            </Typography>
          )}
        </Box>

        <TextField
          label="Google/Microsoft Document Link (Optional)" // Changed label
          variant="outlined"
          fullWidth
          value={microsoftLink}
          onChange={handleMicrosoftLinkChange}
          margin="normal"
          InputLabelProps={{ style: { color: 'white' } }}
          inputProps={{ style: { color: 'white' } }}
          sx={{ '& .MuiOutlinedInput-root': { '& fieldset': { borderColor: 'white' }, '&:hover fieldset': { borderColor: '#d98d2a' }, '&.Mui-focused fieldset': { borderColor: '#d98d2a' } } }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={handleOpenInfoDialog} edge="end" sx={{ color: '#d98d2a' }}>
                  <InfoIcon />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        <Button
          type="submit"
          variant="contained"
          fullWidth
          sx={{ mt: 2, backgroundColor: '#d98d2a', '&:hover': { backgroundColor: '#c69c2f' } }}
          disabled={isUploading}
        >
          {isUploading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={24} color="inherit" />
              {uploadProgress > 0 && uploadProgress < 100 && (
                <Typography variant="body2">Uploading audio... {uploadProgress}%</Typography>
              )}
            </Box>
          ) : (
            'Upload Journal Entry'
          )}
        </Button>
      </form>

      <Dialog open={infoDialogOpen} onClose={handleCloseInfoDialog}>
        <DialogTitle sx={{ backgroundColor: '#333333', color: '#d98d2a' }}>
          How to Add Your Document from Google Docs
        </DialogTitle>
        <DialogContent sx={{ backgroundColor: '#444444', color: '#F5F5F5' }}>
          <DialogContentText component="div" sx={{ color: '#F5F5F5' }}>
            <Typography gutterBottom variant="body1">
              To ensure your document is viewable as a consistent PDF within the journal and still link to the original editable Google Document, please follow these steps:
            </Typography>
            <ol>
              <li>Open your document in Google Docs.</li>
              <li>Go to <strong>File</strong> &gt; <strong>Download</strong>.</li>
              <li>Choose <strong>PDF Document (.pdf)</strong> and save the PDF file to your computer.</li>
              <li>Back on this page, click the <strong>"Choose Scroll (PDF only)"</strong> button and select the PDF file you just downloaded.</li>
              <li>
                Get a shareable link for your original Google Document:
                <ul>
                  <li>In Google Docs, click the <strong>Share</strong> button (usually top right).</li>
                  <li>Under "General access", ensure it's set to "Anyone with the link" and the role is "Viewer" (or "Commenter"/"Editor" if you want others to interact with the original).</li>
                  <li>Click <strong>Copy link</strong>.</li>
                </ul>
              </li>
              <li>Paste this copied link into the <strong>"Google/Microsoft Document Link (Optional)"</strong> field on this form.</li>
            </ol>
            <Typography variant="body2" sx={{ mt: 2 }}>
              This way, other users can view the PDF directly, and you (or others, if permissions allow) can access the original document via the link for edits or comments.
            </Typography>
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ backgroundColor: '#333333' }}>
          <Button onClick={handleCloseInfoDialog} sx={{ color: '#d98d2a' }}>
            Got it!
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default CreateJournalEntry;